import {
  FieldWrapper,
  FormField,
  FormGrid,
  InputSelectionList,
  ModalBody,
  ModalFoot,
  ModalHead,
  SelectionList,
  Text,
} from '@dixa/lib-shared-habla';
import { useCallback, useMemo, useState } from 'react';
import { Form, FormErrors, InjectedFormProps, reduxForm } from 'redux-form';

import {
  BasicQueueSelectItem,
  InputQueuesSelect,
} from 'components/inputQueuesSelect';
import { OptionT } from 'components/powerFlowShared/components/types';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import type { FlowManagerEditModalComponentT } from 'fable/flowEditor';

import type { ContactFormModalStatePropsT } from '../../containers/modals/contactform';
import type {
  FlowChatNodeContactFormPayloadT,
  FlowChatNodeContactFormT,
} from '../nodeTypes';

type PropsT = FlowManagerEditModalComponentT<FlowChatNodeContactFormT> &
  ContactFormModalStatePropsT;

type InjectedProps = InjectedFormProps<FlowChatNodeContactFormT, PropsT>;

const validate = (node: FlowChatNodeContactFormT): FormErrors => {
  let errors = {};
  if (!node) errors;

  if (
    !node.payload ||
    !node.payload.queue ||
    !node.payload.queue.value ||
    node.payload.queue.value === ''
  ) {
    errors = {
      payload: {
        queue: {
          value: 'Required - please select a queue',
        },
      },
    };
  }

  return errors;
};

function ContactFormModal(props: PropsT & InjectedProps) {
  const { fromEmails, handleSubmit, node, nodeLabel, destroy, triggerClose } =
    props;

  function handleSaveClick(values: FlowChatNodeContactFormT): void {
    if (!queue) {
      return;
    }
    const payload: FlowChatNodeContactFormPayloadT = {
      queue: queue,
      fromEmail: fromEmail ?? undefined,
    };
    const nodeOptionsText = `Contact Form ${
      queue.label ? `(${queue.label})` : ''
    }`;
    const nodeOptions: FlowChatNodeContactFormT = {
      ...props.node,
      ...values,
      badges: [],
      icon: '',
      isAction: false,
      isEditable: true,
      isRemovable: true,
      nodeType: FlowNodeTypes.CONTACT_FORM,
      overlay: [],
      payload: payload,
      text: nodeOptionsText,
    };
    props.replaceNode(nodeOptions);
    props.triggerClose();
  }

  const [queue, setQueue] = useState<OptionT | undefined>(node.payload?.queue);
  const [fromEmail, setFromEmail] = useState<string | undefined>(
    node.payload?.fromEmail,
  );
  const emailItems = useMemo(
    () =>
      fromEmails.map(({ email_address, sender_name }) => ({
        id: email_address,
        name: sender_name ? `${sender_name} <${email_address}>` : email_address,
      })),
    [fromEmails],
  );

  const isChanged =
    queue !== node.payload?.queue || fromEmail !== node.payload?.fromEmail;
  function closeModal() {
    destroy();
    triggerClose();
  }
  const handleEmailChange = useCallback((emailAddress: string): void => {
    setFromEmail(emailAddress);
  }, []);

  const handleQueueChange = useCallback(
    (selectedQueue?: BasicQueueSelectItem): void => {
      if (!selectedQueue) {
        return;
      }
      setQueue({
        value: selectedQueue.id,
        label: selectedQueue.name,
      });
    },
    [],
  );

  return (
    <div className="contact-form modal-chatflow-holder">
      <Form onSubmit={handleSubmit(handleSaveClick)}>
        <ModalHead title={nodeLabel} onClose={closeModal} />

        <ModalBody>
          <FormGrid>
            <FormField>
              <FieldWrapper label={'Select queue'} required>
                <InputQueuesSelect
                  required
                  value={queue ? [{ id: queue.value, name: queue.label }] : []}
                  onChange={(e) => handleQueueChange(e.selectedValues[0])}
                />
              </FieldWrapper>
            </FormField>
            <FormField>
              <InputSelectionList
                required={false}
                clearable={true}
                placeholder={'Select an email'}
                onChange={(event) =>
                  event.selectedItem?.value &&
                  handleEmailChange(event.selectedItem?.value.id)
                }
                getItemTitleByValue={(selectItem) => {
                  if (selectItem) {
                    return <Text.Paragraph>{selectItem.name}</Text.Paragraph>;
                  }
                  return <></>;
                }}
                value={
                  fromEmail
                    ? [
                        {
                          id: (emailItems || []).find(
                            (selectItem) => selectItem.id === fromEmail,
                          )?.name as string,
                          name: fromEmail as string,
                        },
                      ]
                    : []
                }
                equalityFn={(
                  first: { id: string; name: string },
                  second: { id: string; name: string },
                ) => first.id === second.id}
              >
                {(emailItems || []).map((selectItem) => (
                  <SelectionList.Item key={selectItem.id} value={selectItem}>
                    {selectItem.name}
                  </SelectionList.Item>
                ))}
              </InputSelectionList>
            </FormField>
          </FormGrid>
        </ModalBody>

        <ModalFoot
          confirmOnClick={handleSubmit(handleSaveClick)}
          confirmName={getConfirmButtonVerb(node)}
          confirmProps={{ disabled: !queue || !isChanged }}
          cancelOnClick={closeModal}
        />
      </Form>
    </div>
  );
}

export default reduxForm<FlowChatNodeContactFormT, PropsT>({
  form: 'chatFlowContactForm', // a unique identifier for this form
  enableReinitialize: true,
  validate,
})(ContactFormModal);
